'use client';

import { useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Typography, Skeleton } from '@mui/material';
import { Box } from '@mui/system';
import { Image } from '@scorenco/components';
import { Types } from '@scorenco/core';

import { GoalAnimation } from '../../components/animations';
import { SquarePartners } from '../../components/SquarePartners';

import { AdScorenco } from './AdScorenco';
import { LastEvent } from './LastEvent';
import { LastEventByTeamDocument } from './LastEvent/LastEvent.query.generated';
import { NextTwoEvents } from './NextTwoEvents';
import { NextTwoEventByTeamDocument } from './NextTwoEvents/NextEvent.query.generated';
import { OtherResults } from './OtherResults';
import { OtherResultsDocument } from './OtherResults/OtherResults.query.generated';
import { RankingTab } from './RankingTab';
import { TeamRankingQueryDocument } from './RankingTab/RankingTab.query.generated';

const scores: (number | null | undefined)[] = [];

type GetTeamByIdData = {
  teams_team_detail_by_pk: Array<{
    id: number;
    team_id: number;
    name_in_club: string;
    name_in_competition: string;
    team_in_season: { squad_picture: string | null } | null;
    team: { nb_fans: number } | null;
  }>;
};

type GetGoalsData = {
  competitions_event: Array<{
    id: number;
    playing_teams: Array<{
      role: string;
      score: number | null;
      team_in_season: { short_name: string } | null;
    }>;
  }>;
};

const style = {
  root: css`
    max-width: 100vw;
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: 0;
    margin-top: 1.2rem;
    padding: 0 100px;

    .right {
      flex-basis: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .left {
      flex-basis: 100%;
    }

    .center {
      margin: 0 2rem;
      flex-basis: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .team-picture {
      height: 15rem;
      width: 100%;
      flex-shrink: 0;
      object-fit: cover;
      object-position: center;
      border-radius: 0.5rem;
      margin-bottom: 1.5rem;
    }
  `,
};

type TeamProps = {
  screen: Types.Clubhouse_Screen;
  clubHouse: Types.Clubhouse_Club_House;
};

export const Team = ({ screen, clubHouse }: TeamProps) => {
  const clubId = clubHouse.club?.id;
  const screenTeamId = screen.team_id;

  const getTeamQuery = useQuery<GetTeamByIdData>(
    gql`
      query GetTeamById($teamId: Int, $clubId: Int) {
        teams_team_detail_by_pk(args: { id: $teamId, club_id: $clubId }) {
          id
          team_id
          name_in_club
          name_in_competition
          team_in_season {
            squad_picture
          }
          team {
            nb_fans
          }
        }
      }
    `,
    {
      variables: {
        teamId: screenTeamId ?? 0,
        clubId: clubId ?? 0,
      },
      skip: !screenTeamId || !clubId,
      context: {
        role: 'anonymous',
      },
    }
  );

  const getGoalsQuery = useQuery<GetGoalsData>(
    gql`
      query GetGoals($teamId: Int!) {
        competitions_event(
          limit: 1
          where: {
            status: { _eq: "live" }
            playing_teams: { team_in_season: { id: { _eq: $teamId } } }
          }
        ) {
          id
          playing_teams(where: { team_in_season: { id: { _eq: $teamId } } }) {
            role
            score
            team_in_season {
              short_name
            }
          }
        }
      }
    `,
    {
      variables: {
        teamId: screenTeamId ?? 0,
      },
      skip: !screenTeamId,
      context: {
        role: 'anonymous',
      },
    }
  );
  const getTeamRankingQuery = useQuery(TeamRankingQueryDocument, {
    variables: {
      teamId: screenTeamId ?? 0,
      clubId: clubId ?? 0,
    },
    skip: !screenTeamId || !clubId,
    context: {
      role: 'anonymous',
    },
  });

  const teamDetails = getTeamQuery.data?.teams_team_detail_by_pk?.[0];
  const currentTeamId = teamDetails?.team_id;

  const getNextTwoEventsQuery = useQuery(NextTwoEventByTeamDocument, {
    variables: {
      teamId: currentTeamId ?? 0,
    },
    skip: !currentTeamId,
    context: {
      role: 'anonymous',
    },
  });

  const getLastEventQuery = useQuery(LastEventByTeamDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      teamId: currentTeamId ?? 0,
    },
    skip: !currentTeamId,
    context: {
      role: 'anonymous',
    },
  });

  const lastEvent =
    getLastEventQuery.data?.competitions_event_detail_by_team_id?.[0];

  const getOtherGames = useQuery(OtherResultsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      eventId: lastEvent?.id ?? 0,
      roundDayId: lastEvent?.round?.id ?? 0,
    },
    skip:
      !lastEvent?.id ||
      !lastEvent?.round?.id ||
      lastEvent.family === 'exhibition',
    context: {
      role: 'anonymous',
    },
  });

  const [newGoal, setNewGoal] = useState(false);
  const currentGoalScore =
    getGoalsQuery.data?.competitions_event?.[0]?.playing_teams?.[0]?.score;
  const nextEvents =
    getNextTwoEventsQuery.data?.competitions_event_detail_by_team_id ?? [];
  const otherGames =
    getOtherGames.data?.competitions_event_detail_by_round_day_id ?? [];
  const rankings =
    getTeamRankingQuery.data?.teams_team_detail_by_pk?.[0]?.team_in_season
      ?.last_competition_with_ranking?.[0]?.rankings ?? [];
  const teamName = teamDetails?.name_in_club;
  const squadPicture = teamDetails?.team_in_season?.squad_picture;
  const nbFans = teamDetails?.team?.nb_fans ?? 0;

  useEffect(() => {
    scores.push(currentGoalScore);
    void getGoalsQuery.refetch();
  }, [currentGoalScore, getGoalsQuery]);

  useEffect(() => {
    scores.push(currentGoalScore);

    const latestScore = scores[scores.length - 1];
    const previousScore = scores[scores.length - 2];

    if (
      typeof latestScore === 'number' &&
      typeof previousScore === 'number' &&
      latestScore > previousScore
    ) {
      setTimeout(() => {
        setNewGoal(true);
      }, 0);
    }
    const timeout = setTimeout(() => {
      setNewGoal(false);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [currentGoalScore]);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 5rem)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h1"
        sx={{
          m: 0,
          flexShrink: 0,
          textAlign: 'center',
          marginTop: '3rem',
        }}
      >
        {teamName || <Skeleton sx={{ maxWidth: '20rem', margin: '0 auto' }} />}
      </Typography>
      <Box css={style.root}>
        {newGoal && teamName && <GoalAnimation teamName={teamName} />}
        <Box className="left">
          {lastEvent && <LastEvent event={lastEvent} />}
          {nextEvents.length > 0 && <NextTwoEvents events={nextEvents} />}
        </Box>
        <Box className="center">
          {squadPicture && (
            <Image className="team-picture" src={`${squadPicture}`} />
          )}
          {otherGames.length > 0 && lastEvent?.family !== 'exhibition' && (
            <OtherResults events={otherGames} />
          )}
          {/* <AdScorenco nbFans={nbFans} /> */}
        </Box>
        {rankings.length > 0 && screenTeamId && clubId && (
          <Box className="right">
            <RankingTab teamId={screenTeamId} clubId={clubId} />
          </Box>
        )}

        {clubId ? <SquarePartners clubId={clubId} /> : null}
      </Box>
    </Box>
  );
};
